import {Dropbox} from '@dropbox/api-v2-client';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {DropboxClientContext} from 'App';
import {snackbarAtom} from 'atoms/snackbar';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {useContext} from 'react';

import {appsQueryKey, setPublishVisibility} from './api';
import {CompanyOsPlatformDetails, PublishVisibility} from './appsApiTypes';
import {CatalogApp} from './catalogApp';

const OWNER_ONLY = 'publish_visibility_owner_only' as const;
const ALL_EMPLOYEES = 'publish_visibility_all_employees' as const;

type SupportedVisibilityTag = typeof OWNER_ONLY | typeof ALL_EMPLOYEES;

const appVisibility = (app: CatalogApp): SupportedVisibilityTag | null => {
  if (app.platform_details?.['.tag'] !== 'company_os') {
    return null;
  }

  const tag = (app.platform_details as CompanyOsPlatformDetails | undefined)?.visibility?.['.tag'];
  return tag === OWNER_ONLY || tag === ALL_EMPLOYEES ? tag : null;
};

export const isPrivateApp = (app: CatalogApp) => appVisibility(app) === OWNER_ONLY;

const visibilityValue = (tag: SupportedVisibilityTag): PublishVisibility => ({
  '.tag': tag,
});

export const useAppVisibility = (app: CatalogApp) => {
  const client = useContext(DropboxClientContext);
  const queryClient = useQueryClient();
  const setSnackbar = useSetAtom(snackbarAtom);
  const currentVisibility = appVisibility(app);
  const targetVisibility =
    currentVisibility === OWNER_ONLY
      ? ALL_EMPLOYEES
      : currentVisibility === ALL_EMPLOYEES
        ? OWNER_ONLY
        : null;
  const mutation = useMutation({
    mutationFn: async () => {
      if (!client || !app.id || !currentVisibility || !targetVisibility) {
        throw new Error('App visibility cannot be changed');
      }

      return setPublishVisibility(client as Dropbox, {
        site_id: app.id,
        visibility: visibilityValue(targetVisibility),
        expected_visibility: visibilityValue(currentVisibility),
        request_token: `visibility-${globalThis.crypto.randomUUID()}`,
      });
    },
    onSuccess: () => {
      setSnackbar({
        text:
          targetVisibility === ALL_EMPLOYEES
            ? t('apps_visibility_all_success')
            : t('apps_visibility_owner_success'),
      });
      void queryClient.invalidateQueries({queryKey: appsQueryKey});
    },
    onError: () => {
      setSnackbar({text: t('apps_visibility_error')});
      void queryClient.invalidateQueries({queryKey: appsQueryKey});
    },
  });

  const canChangeVisibility = Boolean(
    client && app.id && currentVisibility && targetVisibility && !mutation.isPending
  );

  return {
    canChangeVisibility,
    toggleVisibility: mutation.mutate,
    visibilityActionLabel: canChangeVisibility
      ? targetVisibility === ALL_EMPLOYEES
        ? t('apps_visibility_make_all')
        : t('apps_visibility_make_owner')
      : null,
  };
};
